// frontend/src/pages/ProgramasCatalogoPage/ProgramasCatalogoPage.jsx
import React, { useState, useEffect } from 'react';
import { 
    Typography, 
    Table, 
    message, 
    Button, 
    Modal, 
    Form, 
    InputNumber,
    Tag,
    Select,
    Input,
    Row,
    Col,
    List,
    Card,
    Space,
    Tooltip
} from 'antd';
import { EditOutlined, SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as milhasService from '../../services/milhasService';
import './ProgramasCatalogoPage.css';
import { getErrorMessage } from '../../utils/errorHandler';
import { getMobileFullscreenModalProps } from '../../utils/mobileModal';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import LogoManager from '../../components/LogoManager/LogoManager';
import SolicitarProgramaModal from '../../components/SolicitarProgramaModal/SolicitarProgramaModal';

const { Title, Text } = Typography;
const { Option } = Select;

const ProgramasCatalogoPage = () => {
    const [programas, setProgramas] = useState([]);
    const [filteredProgramas, setFilteredProgramas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPrograma, setEditingPrograma] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [solicitarVisible, setSolicitarVisible] = useState(false);
    const [form] = Form.useForm();

    const [messageApi, contextHolder] = message.useMessage();

    // Hook de Responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchProgramas = async () => {
        setLoading(true);
        try {
            const response = await milhasService.getProgramasFidelidade();
            const data = response.data;

            if (Array.isArray(data)) {
                setProgramas(data);
                setFilteredProgramas(data);
            } else {
                messageApi.error('A resposta do servidor para os programas não era válida.');
                setProgramas([]); 
                setFilteredProgramas([]);
            }
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao buscar os programas de fidelidade.'));
            setProgramas([]);
            setFilteredProgramas([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProgramas();
    }, []);

    useEffect(() => {
        const filtered = programas.filter(programa => 
            programa.nome.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredProgramas(filtered);
    }, [searchText, programas]);

    const handleEdit = (programa) => {
        setEditingPrograma(programa);
        const limite = programa.limite_cpfs ?? null;
        form.setFieldsValue({
            limite_cpfs: limite != null ? limite : undefined,
            politica_reset_cpf: programa.politica_reset_cpf || 'ANO_CALENDARIO',
            carencia_dias: programa.carencia_dias ?? undefined,
        });
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingPrograma(null);
        form.resetFields();
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                limite_cpfs: values.limite_cpfs == null || values.limite_cpfs === '' ? null : Number(values.limite_cpfs),
                carencia_dias: values.carencia_dias == null || values.carencia_dias === '' ? null : Number(values.carencia_dias),
            };
            await milhasService.updateProgramaFidelidade(editingPrograma.id, payload);
            messageApi.success('Programa atualizado com sucesso!');
            handleCancel();
            fetchProgramas(); 
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao atualizar o programa.'));
        }
    };

    const politicaLabel = {
        'ANO_CALENDARIO': 'Ano-Calendário',
        'ANO_ROLANTE': 'Ano-Rolante (365 dias)',
        'LISTA_BENEFICIARIOS': 'Lista de Beneficiários (ex: Azul)',
        'APENAS_MONITORAR': 'Apenas monitorar (sem limite oficial)',
    };

    // Colunas para Desktop
    const columns = [
        {
            title: 'Nome do Programa',
            dataIndex: 'nome',
            key: 'nome',
            sorter: (a, b) => a.nome.localeCompare(b.nome),
            render: (nome, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <LogoManager slug={record.slug} type="programa" style={{ width: 64, height: 32, flexShrink: 0 }} />
                    <span>{nome}</span>
                </div>
            ),
        },
        {
            title: 'Política de Reset',
            dataIndex: 'politica_reset_cpf',
            key: 'politica_reset_cpf',
            align: 'center',
            render: (politica) => (
                <Tag color={politica === 'APENAS_MONITORAR' ? 'default' : politica === 'ANO_CALENDARIO' ? 'purple' : 'cyan'}>
                    {politicaLabel[politica] || politica}
                </Tag>
            ),
        },
        {
            title: 'Limite de CPFs',
            dataIndex: 'limite_cpfs',
            key: 'limite_cpfs',
            align: 'center',
            render: (val) => (val == null || val === 0) ? (
                <Tooltip title="Sem bloqueio automático; apenas monitora o uso de CPFs.">
                    <Tag color="default">Apenas monitorar</Tag>
                </Tooltip>
            ) : val,
        },
        {
            title: 'Ação',
            key: 'action',
            align: 'center',
            render: (_, record) => (
                <Button 
                    type="primary" 
                    icon={<EditOutlined />} 
                    onClick={() => handleEdit(record)}
                >
                    Editar
                </Button>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '0' }}>
            {contextHolder}
            
            {/* Header Responsivo */}
            <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={16}>
                    <Title level={2} style={{ margin: 0 }}>Catálogo de Programas</Title>
                    <Text type="secondary">
                        Gerencie as configurações e limites dos programas de fidelidade.
                    </Text>
                    <div>
                        <Button type="link" style={{ padding: 0 }} onClick={() => setSolicitarVisible(true)}>
                            Não encontrou um programa? Solicitar cadastro
                        </Button>
                    </div>
                </Col>
                <Col xs={24} lg={8}>
                    <Input
                        placeholder="Pesquisar programa..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        size="large"
                        style={{ width: '100%' }}
                    />
                </Col>
            </Row>

            {/* Conteúdo: Lista (Mobile) ou Tabela (Desktop) */}
            {(() => {
                const emptyProgramas = (
                    <EmptyStateGuide
                        title={searchText ? 'Nenhum programa encontrado.' : 'Nenhum programa no catálogo.'}
                        description={
                            searchText
                                ? 'Tente outro termo. Se o programa não estiver no catálogo, peça para a equipe cadastrar.'
                                : 'O catálogo de programas de fidelidade aparece aqui para configuração.'
                        }
                        actionLabel={searchText ? 'Solicitar este programa' : undefined}
                        onAction={searchText ? () => setSolicitarVisible(true) : undefined}
                        icon={null}
                    />
                );
                return isMobile ? (
                <List
                    dataSource={filteredProgramas}
                    loading={loading}
                    locale={{ emptyText: emptyProgramas }}
                    renderItem={(item) => (
                        <Card 
                            style={{ marginBottom: 16, overflow: 'hidden' }}
                            bodyStyle={{ padding: 0 }}
                        >
                            <div style={{ padding: 16 }}>
                                <Row justify="space-between" align="top">
                                    <Col flex="auto">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                            <LogoManager slug={item.slug} type="programa" style={{ width: 64, height: 32, flexShrink: 0 }} />
                                            <Text strong style={{ fontSize: 16 }}>{item.nome}</Text>
                                        </div>
                                        
                                        <Space direction="vertical" size={2}>
                                            <div>
                                                <Text type="secondary">Limite CPFs: </Text>
                                                {(item.limite_cpfs == null || item.limite_cpfs === 0) ? (
                                                    <Tag color="default">Apenas monitorar</Tag>
                                                ) : (
                                                    <Text strong>{item.limite_cpfs}</Text>
                                                )}
                                            </div>
                                            <div style={{ marginTop: 4 }}>
                                                <Tag style={{ margin: 0 }} color={item.politica_reset_cpf === 'APENAS_MONITORAR' ? 'default' : item.politica_reset_cpf === 'ANO_CALENDARIO' ? 'purple' : 'cyan'}>
                                                    {politicaLabel[item.politica_reset_cpf] || item.politica_reset_cpf}
                                                </Tag>
                                            </div>
                                        </Space>
                                    </Col>
                                </Row>
                            </div>
                            
                            {/* Botão de Ação Full-Width */}
                            <div 
                                className="programas-action-btn"
                                onClick={() => handleEdit(item)}
                            >
                                <EditOutlined style={{ marginRight: 8 }} /> Editar Configurações
                            </div>
                        </Card>
                    )}
                />
            ) : (
                <Table 
                    columns={columns}
                    dataSource={filteredProgramas}
                    loading={loading}
                    rowKey="id"
                    pagination={{ 
                        showSizeChanger: true,
                        pageSizeOptions: ['15', '50', '100'],
                        defaultPageSize: 15
                    }}
                    locale={{ emptyText: emptyProgramas }}
                />
            );
            })()}

            {editingPrograma && (
                <Modal
                    title={`Editar: ${editingPrograma.nome}`}
                    open={isModalVisible}
                    onOk={handleOk}
                    onCancel={handleCancel}
                    okText="Salvar"
                    cancelText="Cancelar"
                    width={isMobile ? '100%' : 520}
                    {...getMobileFullscreenModalProps(isMobile)}
                >
                    <Form form={form} layout="vertical" name="form_in_modal" size={isMobile ? 'large' : 'middle'}>
                        <Form.Item
                            name="limite_cpfs"
                            label={
                                <span>
                                    Limite de CPFs
                                    <Tooltip title="Use 0 ou deixe vazio para 'apenas monitorar' (sem bloqueio de novas emissões). Programas como AAdvantage não divulgam limite oficial.">
                                        <InfoCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
                                    </Tooltip>
                                </span>
                            }
                        >
                            <InputNumber min={0} placeholder="0 = apenas monitorar" style={{ width: '100%' }} inputMode="numeric" />
                        </Form.Item>
                        <Form.Item
                            name="politica_reset_cpf"
                            label={
                                <span>
                                    Política de Reset do Limite
                                    <Tooltip title="Ano-Calendário: zera em 1º/jan. Ano-Rolante: janela móvel. Lista: benef. simultâneos com carência ao trocar.">
                                        <InfoCircleOutlined style={{ marginLeft: 6, color: '#999' }} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[{ required: true, message: 'Por favor, selecione a política!' }]}
                        >
                            <Select placeholder="Selecione a política">
                                <Option value="ANO_CALENDARIO">Ano-Calendário (1º jan – 31 dez)</Option>
                                <Option value="ANO_ROLANTE">Ano-Rolante (365 dias móveis)</Option>
                                <Option value="LISTA_BENEFICIARIOS">Lista de Beneficiários (ex: Azul)</Option>
                                <Option value="APENAS_MONITORAR">Apenas monitorar (sem limite oficial)</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, curr) => prev.politica_reset_cpf !== curr.politica_reset_cpf}
                        >
                            {({ getFieldValue }) =>
                                getFieldValue('politica_reset_cpf') === 'LISTA_BENEFICIARIOS' ? (
                                    <Form.Item
                                        name="carencia_dias"
                                        label="Carência (dias)"
                                        extra="Dias de espera após remover beneficiário antes de emitir para novo CPF."
                                    >
                                        <InputNumber min={1} max={365} placeholder="Ex: 60" style={{ width: '100%' }} />
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>
                    </Form>
                </Modal>
            )}

            <SolicitarProgramaModal
                visible={solicitarVisible}
                onClose={() => setSolicitarVisible(false)}
                nomeInicial={searchText.trim()}
            />
        </div>
    );
};

export default ProgramasCatalogoPage;